import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Calculator, Building2, Mail, Phone, MapPin, Calendar, Upload, Sparkles, Target, Clock, Check, FileDown, RefreshCw, Instagram, Facebook, Linkedin, Globe } from "lucide-react";
import ImageCarousel from "./components/ImageCarousel";
import { ProgressIndicator, CompactProgress, CurrentStepIndicator } from "./components/ProgressIndicator";
import { 
  LoadingButton, 
  CalculationLoader, 
  SkeletonField, 
  ErrorState, 
  SuccessState, 
  FloatingNotification 
} from "./components/LoadingStates";
import { 
  trackEvent,
  trackFormStart, 
  trackFormStep, 
  trackFormComplete, 
  trackFormError, 
  trackFormAbandonment,
  trackButtonClick, 
  trackLanguageChange, 
  trackCalculationAttempt, 
  trackCalculationSuccess,
  calculateLeadScore,
  getLeadQuality
} from "./lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Use relative URL for API calls so it works in all environments (preview, deployed, local)
// In Kubernetes, the ingress routes /api/* to the backend automatically
const API = '/api';

// Image collections for carousels - Updated with latest Captif project images
const heroImages = [
  "https://customer-assets.emergentagent.com/job_design-calc/artifacts/7fkqbfnw_CAPTIF_EuropAssistance_x10.jpg",
  "https://customer-assets.emergentagent.com/job_design-calc/artifacts/jqzb991z_Desdalex-%28c%29-Captif-2019-02_LR.jpg", 
  "https://customer-assets.emergentagent.com/job_design-calc/artifacts/ws72g7i1_The%20Beacon%20-%20CAPTIF%20-%20designbuild%20-%20001.jpg"
];

const ctaImages = [
  "https://customer-assets.emergentagent.com/job_captif-planner/artifacts/oo83tx5z_backbone_509407-A_040_WEB.jpg",
  "https://customer-assets.emergentagent.com/job_captif-planner/artifacts/sd36b8ag_backbone_516893-A_010_WEB.jpg",
  "https://customer-assets.emergentagent.com/job_captif-planner/artifacts/4mvi004p_backbone_547039-A_001_WEB.jpg",
  "https://customer-assets.emergentagent.com/job_captif-planner/artifacts/ctlkygav_backbone_547039-A_108_WEB.jpg",
  "https://customer-assets.emergentagent.com/job_captif-planner/artifacts/n5y3xrmu_%C2%A9%20CAPTIF%20-%20Teal%20Partners_21.jpg",
  "https://customer-assets.emergentagent.com/job_captif-planner/artifacts/0murs36m_backbone_507669-A_019_WEB.jpg",
  "https://customer-assets.emergentagent.com/job_captif-planner/artifacts/z8rx584l_backbone_508816-A_148_WEB.jpg",
  "https://customer-assets.emergentagent.com/job_captif-planner/artifacts/ahx4jsoe_CAPTIF_EuropAssistance_x10.jpg",
  "https://customer-assets.emergentagent.com/job_captif-planner/artifacts/cppp8ffi_Desdalex-%28c%29-Captif-2019-02_LR.jpg"
];

// Language translations
const translations = {
  en: {
    title: "Office Fitout Budget Calculator",
    subtitle: "Get an instant budget estimate for your office interior design project using our professional calculator based on market benchmarks and expertise.",
    projectParams: "Project Parameters",
    projectParamsDesc: "Fill in the details about your office space requirements",
    surface: "Total Net Surface (m²)",
    workplaceConcept: "Workplace Concept",
    finishLevel: "Interior Finish Level", 
    furnitureStandard: "Standard Furniture",
    sustainableSolutions: "Sustainable Solutions",
    sustainableYes: "Yes, include eco-friendly materials and techniques",
    sustainableNo: "No, standard approach",
    floorPlanUpload: "Floor Plan Upload (Optional)",
    floorPlanHelper: "📐 Upload your floor plan for more accurate AI analysis",
    uploadFloorPlan: "Choose Floor Plan File",
    floorPlanUploaded: "Floor plan uploaded successfully",
    additionalInfo: "Additional Project Details",
    additionalInfoPlaceholder: "e.g. Sustainability requirements, technology integration needs, accessibility features, special design requests, timeline constraints...",
    aiHelper: "🤖 AI will analyze these details to provide more accurate pricing and insights",
    calculateBtn: "Calculate Budget",
    resetBtn: "Reset",
    contactRequired: "Contact Details Required",
    contactDesc: "Please provide your contact information to receive your personalized budget estimate.",
    firstName: "First Name",
    lastName: "Last Name", 
    company: "Company",
    email: "Email",
    phone: "Phone (Optional)",
    terms: "I agree that Captif may contact me regarding this project. My information will not be added to mailing lists or used for newsletters.",
    getBudget: "Get My Budget Estimate",
    budgetEstimate: "Your Budget Estimate",
    pricePerM2: "Price per square meter",
    totalBudget: "Total Project Budget",
    captifAnalysis: "🏢 Captif Agent Analysis",
    roiCalculator: "💰 ROI Analysis",
    employees: "Estimated Employees",
    costPerEmployee: "Cost per Employee",
    productivityGain: "Expected Productivity Gain",
    energySavings: "Annual Energy Savings",
    bookConsultation: "Book Free Consultation",
    getPdfReport: "Get PDF Report",
    downloadPdf: "Download PDF Report",
    pdfDescription: "Receive a detailed PDF report with your budget breakdown and AI analysis via email",
    readyTransform: "Ready to Transform Your Office?",
    // Open/Semi-open/Closed options
    workplaceOpen: "Open (very few offices)",
    workplaceSemiOpen: "Semi-open (some closed offices, some open plan)",
    workplaceClosed: "Closed (mostly closed offices)",
    // Finish levels
    finishHigh: "High (high-end materials, lots of bespoke furniture)",
    finishMedium: "Medium (normal interior with quality materials)",
    finishBasic: "Basic (no bespoke furniture, basic materials)",
    // Furniture standards
    furnitureHigh: "High (all new furniture)",
    furnitureMedium: "Medium (50% reuse)",
    furnitureLow: "Low (almost all reuse)"
  },
  nl: {
    title: "Kantoorinrichting Budget Calculator",
    subtitle: "Krijg direct een budgetschatting voor uw kantoorinterieur project met onze professionele calculator gebaseerd op marktbenchmarks en expertise.",
    projectParams: "Project Parameters",
    projectParamsDesc: "Vul de details in over uw kantoorruimte vereisten",
    surface: "Totale Netto Oppervlakte (m²)",
    workplaceConcept: "Werkplek Concept", 
    finishLevel: "Interieur Afwerking Niveau",
    furnitureStandard: "Meubilair Standaard",
    sustainableSolutions: "Duurzame Oplossingen",
    sustainableYes: "Ja, milieuvriendelijke materialen en technieken",
    sustainableNo: "Nee, standaard aanpak",
    floorPlanUpload: "Plattegrond Upload (Optioneel)",
    floorPlanHelper: "📐 Upload uw plattegrond voor nauwkeurigere AI analyse",
    uploadFloorPlan: "Kies Plattegrond Bestand",
    floorPlanUploaded: "Plattegrond succesvol geüpload",
    additionalInfo: "Aanvullende Project Details",
    additionalInfoPlaceholder: "bijv. Duurzaamheidseisen, technologie integratie, toegankelijkheid, speciale ontwerp wensen, tijdsdruk...",
    aiHelper: "🤖 AI analyseert deze details voor nauwkeurigere prijzen en inzichten",
    calculateBtn: "Bereken Budget",
    resetBtn: "Reset",
    contactRequired: "Contactgegevens Vereist",
    contactDesc: "Geef uw contactinformatie om uw persoonlijke budgetschatting te ontvangen.",
    firstName: "Voornaam",
    lastName: "Achternaam",
    company: "Bedrijf", 
    email: "E-mail",
    phone: "Telefoon (Optioneel)",
    terms: "Ik ga akkoord met het verstrekken van mijn contactgegevens aan Captif Office Architects uitsluitend voor projectconsultatie. Deze informatie wordt niet gebruikt voor marketing of reclame.",
    getBudget: "Krijg Mijn Budget Schatting",
    budgetEstimate: "Uw Budget Schatting",
    pricePerM2: "Prijs per vierkante meter",
    totalBudget: "Totaal Project Budget",
    captifAnalysis: "🏢 Captif Agent Analyse",
    roiCalculator: "💰 ROI Analyse",
    employees: "Geschatte Werknemers",
    costPerEmployee: "Kosten per Werknemer", 
    productivityGain: "Verwachte Productiviteitswinst",
    energySavings: "Jaarlijkse Energiebesparing",
    bookConsultation: "Boek Gratis Consultatie",
    getPdfReport: "PDF Rapport Ontvangen",
    downloadPdf: "PDF Rapport Downloaden",
    pdfDescription: "Ontvang een gedetailleerd PDF-rapport met uw budgetoverzicht en AI-analyse per e-mail",
    readyTransform: "Klaar om Uw Kantoor Te Transformeren?",
    workplaceOpen: "Open (zeer weinig kantoren)",
    workplaceSemiOpen: "Semi-open (enkele gesloten kantoren, deels open)",
    workplaceClosed: "Gesloten (meestal gesloten kantoren)",
    finishHigh: "Hoog (hoogwaardige materialen, veel maatwerk)",
    finishMedium: "Gemiddeld (normale interieur met kwaliteitsmaterialen)",
    finishBasic: "Basis (geen maatwerk, basis materialen)",
    furnitureHigh: "Hoog (alle nieuwe meubels)",
    furnitureMedium: "Gemiddeld (50% hergebruik)",
    furnitureLow: "Laag (bijna alle hergebruik)"
  },
  fr: {
    title: "Calculateur Budget Aménagement Bureau", 
    subtitle: "Obtenez instantanément une estimation budgétaire pour votre projet d'aménagement intérieur de bureau avec notre calculateur professionnel basé sur les références du marché et l'expertise.",
    projectParams: "Paramètres du Projet",
    projectParamsDesc: "Remplissez les détails concernant vos exigences d'espace bureau",
    surface: "Surface Nette Totale (m²)",
    workplaceConcept: "Concept d'Espace de Travail",
    finishLevel: "Niveau de Finition Intérieure", 
    furnitureStandard: "Standard Mobilier",
    sustainableSolutions: "Solutions Durables",
    sustainableYes: "Oui, matériaux écologiques et techniques vertes",
    sustainableNo: "Non, approche standard",
    floorPlanUpload: "Upload Plan d'Étage (Optionnel)",
    floorPlanHelper: "📐 Téléchargez votre plan pour une analyse IA plus précise",
    uploadFloorPlan: "Choisir Fichier Plan",
    floorPlanUploaded: "Plan téléchargé avec succès",
    additionalInfo: "Détails Additionnels du Projet",
    additionalInfoPlaceholder: "ex. Exigences durabilité, intégration technologique, accessibilité, demandes design spéciales, contraintes temps...",
    aiHelper: "🤖 L'IA analysera ces détails pour des prix et insights plus précis",
    calculateBtn: "Calculer Budget",
    resetBtn: "Reset",
    contactRequired: "Coordonnées Requises",
    contactDesc: "Veuillez fournir vos informations de contact pour recevoir votre estimation budgétaire personnalisée.",
    firstName: "Prénom",
    lastName: "Nom",
    company: "Entreprise",
    email: "Email", 
    phone: "Téléphone (Optionnel)",
    terms: "J'accepte de fournir mes coordonnées à Captif Office Architects uniquement à des fins de consultation projet. Ces informations ne seront pas utilisées pour marketing ou publicité.",
    getBudget: "Obtenir Mon Estimation Budget",
    budgetEstimate: "Votre Estimation Budget",
    pricePerM2: "Prix par mètre carré",
    totalBudget: "Budget Total Projet",
    captifAnalysis: "🏢 Analyse Agent Captif", 
    roiCalculator: "💰 Analyse ROI",
    employees: "Employés Estimés",
    costPerEmployee: "Coût par Employé",
    productivityGain: "Gain Productivité Attendu", 
    energySavings: "Économies Énergie Annuelles",
    bookConsultation: "Réserver Consultation Gratuite",
    getPdfReport: "Obtenir Rapport PDF",
    downloadPdf: "Télécharger Rapport PDF",
    pdfDescription: "Recevez un rapport PDF détaillé avec votre répartition budgétaire et analyse IA par email",
    readyTransform: "Prêt à Transformer Votre Bureau?",
    workplaceOpen: "Ouvert (très peu de bureaux)",
    workplaceSemiOpen: "Semi-ouvert (quelques bureaux fermés, partiellement ouvert)",
    workplaceClosed: "Fermé (principalement bureaux fermés)",
    finishHigh: "Élevé (matériaux haut de gamme, beaucoup sur mesure)",
    finishMedium: "Moyen (intérieur normal avec matériaux qualité)",
    finishBasic: "Basique (pas de sur mesure, matériaux de base)",
    furnitureHigh: "Élevé (tout nouveau mobilier)",
    furnitureMedium: "Moyen (50% réutilisation)",
    furnitureLow: "Bas (presque tout réutilisé)"
  }
};

const Home = () => {
  // Enhanced state management for multi-step form
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps] = useState(4);
  const [formStarted, setFormStarted] = useState(false);
  
  const [formData, setFormData] = useState({
    surface: '',
    workplace_concept: '',
    finish_level: '',
    furniture_standard: '',
    sustainable_solutions: false,
    floor_plan_file: null,
    additional_info: ''
  });
  
  const [contactData, setContactData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: ''
  });
  
  const [companyValidationStatus, setCompanyValidationStatus] = useState({
    loading: false,
    verified: false,
    confidence: '',
    message: '',
    method: '',
    allow_continue: true
  });
  
  const [result, setResult] = useState(null);
  const [budgetFeedback, setBudgetFeedback] = useState(null); // 'yes' or 'too-high'
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState('preparing');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [language, setLanguage] = useState('en');
  const [previousLanguage, setPreviousLanguage] = useState('en');
  const [notifications, setNotifications] = useState([]);
  const [leadScore, setLeadScore] = useState(0);
  const [leadQuality, setLeadQuality] = useState('cold');
  
  // Analytics and tracking
  const [stepStartTime, setStepStartTime] = useState(Date.now());
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);

  // Get current translations
  const t = translations[language];

  // Enhanced input handling with analytics and validation
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
    
    // Clear errors
    setError('');
    setFormErrors(prev => ({
      ...prev,
      [field]: null
    }));
    
    // Track form interaction
    if (!formStarted) {
      setFormStarted(true);
      trackFormStart('budget_calculator');
    }
    
    // Track step progress
    trackFormStep('budget_calculator', currentStep, {
      field_changed: field,
      field_value: typeof value === 'boolean' ? value : (value?.length || 0)
    });
    
    // Update lead score in real-time
    const updatedFormData = { ...formData, [field]: value };
    const score = calculateLeadScore({ ...updatedFormData, contact_info: contactData });
    setLeadScore(score);
    setLeadQuality(getLeadQuality(score));
  }, [formData, contactData, currentStep, formStarted]);

  // Enhanced validation functions
  const validateCurrentStep = useCallback(() => {
    switch (currentStep) {
      case 1:
        return formData.surface && parseFloat(formData.surface) >= 100;
      case 2:
        return formData.workplace_concept && formData.finish_level && formData.furniture_standard;
      case 3:
        return true; // Step 3 is optional
      case 4:
        return validateContactForm();
      default:
        return false;
    }
  }, [currentStep, formData, contactData]);

  const validateForm = () => {
    if (!formData.surface || formData.surface <= 0) {
      setError('Please enter a valid surface area');
      return false;
    }
    if (!formData.workplace_concept) {
      setError('Please select a workplace concept');
      return false;
    }
    if (!formData.finish_level) {
      setError('Please select a finish level');
      return false;
    }
    if (!formData.furniture_standard) {
      setError('Please select a furniture standard');
      return false;
    }
    return true;
  };

  const handleCalculate = async () => {
    if (!validateForm()) return;
    setCurrentStep(4); // Go to contact step
    trackFormStep('budget_calculator', 4, {
      navigation: 'calculate_clicked'
    });
  };

  const handleContactInputChange = (field, value) => {
    setContactData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    
    // Clear company validation when user types
    if (field === 'company') {
      setCompanyValidationStatus({
        loading: false,
        verified: false,
        confidence: '',
        message: '',
        method: '',
        allow_continue: true
      });
    }
    
    // Real-time email validation
    if (field === 'email' && value.trim() && !isValidEmail(value)) {
      // Don't show error immediately, wait for them to finish typing
      // But the validation will catch it on submit
    }
  };

  const handleCompanyValidation = async () => {
    const companyName = contactData.company.trim();
    
    if (!companyName || companyName.length < 2) {
      return;
    }
    
    setCompanyValidationStatus(prev => ({
      ...prev,
      loading: true,
      message: ''
    }));
    
    try {
      const response = await axios.post(`${API}/validate-company`, {
        company_name: companyName
      });
      
      const data = response.data;
      
      setCompanyValidationStatus({
        loading: false,
        verified: data.verified || false,
        confidence: data.confidence || 'low',
        message: data.message || '',
        method: data.method || '',
        allow_continue: data.allow_continue !== false,
        found_in: data.found_in || ''
      });
      
      console.log('Company validation result:', data);
    } catch (error) {
      console.error('Company validation error:', error);
      setCompanyValidationStatus({
        loading: false,
        verified: false,
        confidence: 'low',
        message: 'Kon verificatie niet uitvoeren, maar u kunt doorgaan',
        method: 'error',
        allow_continue: true
      });
    }
  };

  const validateContactForm = useCallback(() => {
    // Only company name is required now
    if (!contactData.company.trim()) {
      return false;
    }
    
    // Company must be verified or allow_continue must be true
    if (!companyValidationStatus.verified && !companyValidationStatus.allow_continue) {
      return false;
    }
    
    // Email is optional but must be valid if provided
    if (contactData.email.trim() && !isValidEmail(contactData.email)) {
      return false;
    }
    
    // Terms must be agreed ONLY if email is provided
    if (contactData.email.trim() && !agreedToTerms) {
      return false;
    }
    
    return true;
  }, [contactData.company, contactData.email, agreedToTerms, companyValidationStatus]);

  const handleFinalCalculate = async () => {
    // Validate contact form with error messages
    if (!contactData.company.trim()) {
      setError('Vul alstublieft uw bedrijfsnaam in / Please enter your company name');
      addNotification('error', 'Bedrijfsnaam is verplicht');
      return;
    }
    
    // Check if company is verified
    if (!companyValidationStatus.verified && !companyValidationStatus.allow_continue) {
      setError('Bedrijfsnaam kon niet geverifieerd worden. Controleer de spelling.');
      addNotification('error', 'Bedrijfsnaam niet geverifieerd');
      return;
    }
    
    // Email is optional but must be valid if provided
    if (contactData.email.trim() && !isValidEmail(contactData.email)) {
      setError('Vul alstublieft een geldig email adres in / Please enter a valid email address');
      addNotification('error', 'Ongeldig email adres');
      return;
    }
    
    // Optional: Validate email domain if email is provided (async)
    if (contactData.email.trim()) {
      const emailValidation = await isValidEmail(contactData.email);
      if (!emailValidation.valid) {
        const errorMsg = emailValidation.error === "Domain does not exist" 
          ? 'Dit email domein bestaat niet. Controleer uw email adres.'
          : emailValidation.error === "No mail servers found for this domain"
          ? 'Dit email domein kan geen emails ontvangen. Gebruik een ander email adres.'
          : 'Vul een geldig email adres in (bijv. naam@bedrijf.com)';
        setError(errorMsg);
        addNotification('error', errorMsg);
        return;
      }
      
      // Terms must be agreed if email is provided
      if (!agreedToTerms) {
        setError('Ga akkoord met de voorwaarden wanneer u uw email opgeeft');
        addNotification('error', 'Voorwaarden moeten worden geaccepteerd');
        return;
      }
    }
    
    setLoading(true);
    setLoadingStage('preparing');
    setLoadingProgress(0);
    setError('');
    
    try {
      // Track calculation attempt
      const leadData = trackCalculationAttempt({ ...formData, contact_info: contactData });
      
      let floorPlanFileId = null;
      
      // Stage 1: Upload floor plan if provided
      if (formData.floor_plan_file) {
        setLoadingStage('analyzing');
        setLoadingProgress(25);
        
        const formDataUpload = new FormData();
        formDataUpload.append('file', formData.floor_plan_file);
        
        const uploadResponse = await axios.post(`${API}/upload-floorplan`, formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        floorPlanFileId = uploadResponse.data.file_id;
        setLoadingProgress(50);
      } else {
        setLoadingProgress(30);
      }
      
      // Stage 2: Calculate budget with smooth progress
      setLoadingStage('calculating');
      setLoadingProgress(60);
      
      // Animate progress while waiting for response
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev < 85) return prev + 1;
          return prev;
        });
      }, 150);
      
      const response = await axios.post(`${API}/calculate`, {
        surface: parseFloat(formData.surface),
        workplace_concept: formData.workplace_concept,
        finish_level: formData.finish_level,
        furniture_standard: formData.furniture_standard,
        sustainable_solutions: formData.sustainable_solutions,
        floor_plan_file_id: floorPlanFileId,
        additional_info: formData.additional_info,
        contact_info: contactData,
        _timestamp: Date.now() // Cache busting parameter
      }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      clearInterval(progressInterval);
      setLoadingProgress(90);
      
      // Stage 3: Finalize
      setLoadingStage('finalizing');
      setLoadingProgress(95);
      await new Promise(resolve => setTimeout(resolve, 300));
      setLoadingProgress(100);
      
      // Small delay for UX
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setResult(response.data);
      setShowContactForm(false);
      setBudgetFeedback(null); // Reset budget feedback for new calculation
      
      // Scroll to top of results
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      
      // Track successful calculation
      trackCalculationSuccess(response.data, leadData);
      trackFormComplete('budget_calculator', {
        lead_score: leadData.leadScore,
        lead_quality: leadData.leadQuality,
        total_budget: response.data.total_budget,
        price_per_m2: response.data.price_per_m2
      });
      
    } catch (e) {
      console.error(e);
      let errorMessage = 'Error calculating budget. Please try again.';
      
      if (e.response?.data?.detail) {
        errorMessage = `Error: ${e.response.data.detail}`;
      }
      
      setError(errorMessage);
      trackFormError('budget_calculator', errorMessage, 'calculation');
      addNotification('error', errorMessage);
    } finally {
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStage('preparing');
    }
  };

  // Notification system - MOVED UP for use in other functions
  const addNotification = useCallback((type, message) => {
    const notification = {
      id: Date.now(),
      type,
      message
    };
    setNotifications(prev => [...prev, notification]);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // European currency formatting function
  const formatEuropeanCurrency = useCallback((amount) => {
    // Format number with European style: 1.000.000,00
    const formatted = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return formatted;
  }, []);

  // Email validation function with domain verification
  const isValidEmail = useCallback(async (email) => {
    // First check format with regex
    const emailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;
    if (!emailRegex.test(email.trim())) {
      return { valid: false, error: "Invalid email format" };
    }
    
    // Then verify domain has mail servers
    try {
      const response = await fetch(`/api/validate-email?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        // If API fails, allow email (fail open)
        return { valid: true, warning: "Could not verify domain" };
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      // Network error - allow email through
      console.warn('Email validation API error:', error);
      return { valid: true, warning: "Could not verify domain" };
    }
  }, []);

  // Enhanced language change with analytics
  const handleLanguageChange = useCallback((newLanguage) => {
    const oldLanguage = language;
    setPreviousLanguage(oldLanguage);
    setLanguage(newLanguage);
    
    trackLanguageChange(oldLanguage, newLanguage);
    
    // Show notification
    addNotification('success', `Language changed to ${newLanguage.toUpperCase()}`);
  }, [language]);

  // Step navigation with validation feedback
  const goToNextStep = useCallback(() => {
    // Validate current step and provide specific feedback
    let isValid = true;
    let errorMessage = '';

    switch (currentStep) {
      case 1:
        if (!formData.surface) {
          errorMessage = 'Please enter the surface area';
          isValid = false;
        } else if (parseFloat(formData.surface) < 100) {
          errorMessage = 'Surface area must be at least 100m²';
          isValid = false;
        }
        break;
      case 2:
        const missingFields = [];
        if (!formData.workplace_concept) missingFields.push('Workplace Concept');
        if (!formData.finish_level) missingFields.push('Interior Finish Level');
        if (!formData.furniture_standard) missingFields.push('Furniture Standard');
        
        if (missingFields.length > 0) {
          errorMessage = `Please select: ${missingFields.join(', ')}`;
          isValid = false;
        }
        break;
      case 3:
        // Step 3 is optional, always valid
        isValid = true;
        break;
      default:
        isValid = false;
    }

    if (!isValid) {
      addNotification('error', errorMessage);
      return;
    }

    // Proceed to next step
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      setStepStartTime(Date.now());
      trackFormStep('budget_calculator', currentStep + 1, {
        navigation: 'next',
        time_on_step: Date.now() - stepStartTime
      });
      
      // Scroll to top smoothly after step change
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, totalSteps, stepStartTime, formData, addNotification]);

  const goToPrevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setStepStartTime(Date.now());
      trackFormStep('budget_calculator', currentStep - 1, {
        navigation: 'back'
      });
      
      // Scroll to top smoothly after step change
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, stepStartTime]);

  const handleGeneratePDF = async () => {
    if (!result) return;
    
    setPdfLoading(true);
    
    try {
      const response = await fetch(`/api/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          surface: formData.surface,
          workplace_concept: formData.workplace_concept,
          finish_level: formData.finish_level,
          furniture_standard: formData.furniture_standard,
          sustainable_solutions: formData.sustainable_solutions,
          additional_info: formData.additional_info || "",
          floor_plan_file_id: null,
          contact_info: {
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            email: contactData.email,
            phone: contactData.phone || "",
            company: contactData.company || "",
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        addNotification('success', `PDF report sent to ${contactData.email}! Check your inbox.`);
        
        // Track PDF download
        trackEvent('pdf_downloaded', {
          total_budget: result.total_budget,
          surface: formData.surface,
          email: contactData.email
        });
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('PDF generation failed:', errorData);
        throw new Error(errorData.detail || 'PDF generation failed');
      }
    } catch (err) {
      console.error('PDF generation error:', err);
      addNotification('error', `Failed to generate PDF: ${err.message}. Please try again or contact us directly.`);
    } finally {
      setPdfLoading(false);
    }
  };

  const resetCalculator = () => {
    // Track form abandonment if partially filled
    if (formStarted && !result) {
      trackFormAbandonment('budget_calculator', currentStep, {
        surface: formData.surface,
        fields_completed: Object.keys(touchedFields).length
      });
    }

    // Clear all state
    setCurrentStep(1);
    setFormStarted(false);
    setFormData({
      surface: '',
      workplace_concept: '',
      finish_level: '',
      furniture_standard: '',
      sustainable_solutions: false,
      floor_plan_file: null,
      additional_info: ''
    });
    setContactData({
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      phone: ''
    });
    setCompanyValidationStatus({
      loading: false,
      verified: false,
      confidence: '',
      message: '',
      method: '',
      allow_continue: true
    });
    setResult(null);
    setError('');
    setShowContactForm(false);
    setAgreedToTerms(false);
    setTouchedFields({});
    setFormErrors({});
    setLeadScore(0);
    setLeadQuality('cold');
    setStepStartTime(Date.now());
    
    // Clear loading states
    setLoading(false);
    setLoadingProgress(0);
    setLoadingStage('preparing');
    setBudgetFeedback(null);
    setNotifications([]);
    
    // Force clear any browser caches
    if (window.sessionStorage) {
      window.sessionStorage.clear();
    }
    if (window.localStorage) {
      // Clear only calculator-related items
      const keys = Object.keys(window.localStorage);
      keys.forEach(key => {
        if (key.includes('calculator') || key.includes('budget') || key.includes('captif')) {
          window.localStorage.removeItem(key);
        }
      });
    }
    
    addNotification('info', 'Calculator reset successfully');
    
    // Scroll to top smoothly after reset
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Force a small delay to ensure all state is cleared
    setTimeout(() => {
      // Additional cleanup if needed
    }, 100);
  };

  return (
    <div className="min-h-screen bg-brand-light-grey">
      {/* Header */}
      <header className="bg-brand-light-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-32 relative">
            {/* Logo centered in the middle */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <img 
                src="https://customer-assets.emergentagent.com/job_lets-continue-19/artifacts/k3h60ezg_CAPTIF_Logo_Black.png" 
                alt="Captif Office Architects Logo" 
                className="h-28 w-auto" 
              />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Corrected to Match Design */}
      <section className="bg-brand-light-grey py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          {/* Hero Heading - Corrected */}
          <h1 className="mb-3 leading-tight">
            <span className="block text-base md:text-lg text-brand-grey font-light uppercase tracking-wide mb-3">
              DREAMING OF A NEW OFFICE INTERIOR? USE OUR
            </span>
            <span className="block text-3xl md:text-4xl lg:text-5xl font-bold text-brand-black mb-4">
              office fitout budget calculator
            </span>
          </h1>
          
          {/* Subtitle - Smaller text */}
          <p className="text-sm md:text-base text-brand-grey max-w-3xl mx-auto leading-relaxed">
            Get an instant budget estimate for your office interior design project using our professional calculator based on market benchmarks and expertise.
          </p>
        </div>
      </section>

      {/* Floating Notifications */}
      {notifications.map(notification => (
        <FloatingNotification
          key={notification.id}
          type={notification.type}
          message={notification.message}
          onClose={() => removeNotification(notification.id)}
        />
      ))}

      {/* Calculator Section - Reduced spacing */}
      <section className="py-4 px-4 sm:px-6 lg:px-8 bg-brand-light-grey">
        <div className="max-w-4xl mx-auto">

          <div className="max-w-4xl mx-auto">
            {/* Progress Indicator - Hide when results are shown */}
            {!result && (
              <div className="mb-8 animate-fade-in">
                {/* Removed 1/4 counter - duplicate */}
                <div className="hidden md:block">
                  <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />
                </div>
                <div className="md:hidden">
                  <CompactProgress currentStep={currentStep} totalSteps={totalSteps} />
                </div>
              </div>
            )}

            {/* Premium project detection removed */}

            {/* Loading State */}
            {loading && (
              <div className="mb-8">
                <Card className="shadow-lg border-gray-200">
                  <CardContent>
                    <CalculationLoader 
                      stage={loadingStage} 
                      progress={loadingProgress} 
                    />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Calculator Form - Square corners to match design */}
            {!loading && !result && (
              <Card className="shadow-none border-2 border-gray-300 rounded-none bg-gray-50/30 animate-fade-in mx-auto max-w-3xl">
                <CardHeader className="pb-6">
                  <div className="text-center">
                    <div>
                      <CardTitle className="text-brand-black text-2xl font-bold mb-2">
                        {currentStep === 1 ? t.projectParams : currentStep === 2 ? 'Design Preferences' : currentStep === 3 ? 'AI Analysis & Floor Plan' : 'Contact Information'}
                      </CardTitle>
                      <CardDescription className="text-brand-grey">
                        {currentStep === 1 && t.projectParamsDesc}
                        {currentStep === 2 && 'Configure your workplace concept, finishes, and furniture preferences'}
                        {currentStep === 3 && 'Upload floor plan and provide details for AI workplace analysis'}
                        {currentStep === 4 && 'Get your budget estimate instantly - just one click away!'}
                      </CardDescription>
                    </div>
                    
                    {/* Time indicator for current step */}
                    {formStarted && (
                      <div className="text-right text-xs text-brand-grey">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Step {currentStep}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Step 1: Basic Project Parameters */}
                  {currentStep === 1 && (
                    <div className="space-y-6 animate-slide-in-right">
                      <div>
                        <Label htmlFor="surface" className="text-sm font-medium text-brand-black flex items-center space-x-2">
                          <span>{t.surface}</span>
                          <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative mt-1">
                          <Input
                            id="surface"
                            type="number"
                            placeholder="e.g. 500"
                            value={formData.surface}
                            onChange={(e) => handleInputChange('surface', e.target.value)}
                            className={`field-focus touch-optimization ${
                              formErrors.surface ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-brand-gold'
                            }`}
                            data-testid="surface-input"
                            min="1"
                            step="0.1"
                          />
                          <div className="absolute right-8 top-1/2 -translate-y-1/2 text-sm text-brand-grey pointer-events-none">
                            m²
                          </div>
                        </div>
                        {formErrors.surface && (
                          <p className="text-red-500 text-sm mt-1 animate-fade-in">{formErrors.surface}</p>
                        )}
                        {formData.surface && parseFloat(formData.surface) > 0 && (
                          <>
                            {parseFloat(formData.surface) < 100 ? (
                              <p className="text-red-500 text-sm mt-1 animate-fade-in">
                                Projects under 100m² are outside our area of expertise. Please contact us directly for smaller projects.
                              </p>
                            ) : (
                              <p className="text-green-600 text-sm mt-1 animate-fade-in">
                                Excellent! {formData.surface}m² is perfectly within our expertise range
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Design Preferences */}
                  {currentStep === 2 && (
                    <div className="space-y-6 no-dropdown-animations">
                      <div>
                        <Label htmlFor="workplace_concept" className="text-sm font-medium text-brand-black flex items-center space-x-2">
                          <span>{t.workplaceConcept}</span>
                          <span className="text-red-500">*</span>
                        </Label>
                        <select
                          id="workplace_concept"
                          value={formData.workplace_concept}
                          onChange={(e) => handleInputChange('workplace_concept', e.target.value)}
                          className={`mt-1 w-full p-3 border bg-white field-focus touch-optimization ${
                            formErrors.workplace_concept ? 'border-red-500' : 'border-gray-300'
                          }`}
                          data-testid="workplace-concept-select"
                        >
                          <option value="">{language === 'en' ? 'Select concept type' : language === 'nl' ? 'Selecteer concept type' : 'Sélectionner le type de concept'}</option>
                          <option value="open">{t.workplaceOpen}</option>
                          <option value="semi-open">{t.workplaceSemiOpen}</option>
                          <option value="closed">{t.workplaceClosed}</option>
                        </select>
                        {formData.workplace_concept && (
                          <p className="text-green-600 text-sm mt-1 animate-fade-in">
                            {formData.workplace_concept === 'open' ? 'Open concept promotes collaboration' : 
                               formData.workplace_concept === 'semi-open' ? 'Semi-open balances privacy and collaboration' : 
                               'Closed offices provide maximum privacy'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="finish_level" className="text-sm font-medium text-brand-black flex items-center space-x-2">
                          <span>{t.finishLevel}</span>
                          <span className="text-red-500">*</span>
                        </Label>
                        <select
                          id="finish_level"
                          value={formData.finish_level}
                          onChange={(e) => handleInputChange('finish_level', e.target.value)}
                          className={`mt-1 w-full p-3 border bg-white field-focus touch-optimization ${
                            formErrors.finish_level ? 'border-red-500' : 'border-gray-300'
                          }`}
                          data-testid="finish-level-select"
                        >
                          <option value="">{language === 'en' ? 'Select finish level' : language === 'nl' ? 'Selecteer afwerking niveau' : 'Sélectionner niveau finition'}</option>
                          <option value="basic">{t.finishBasic}</option>
                          <option value="medium">{t.finishMedium}</option>
                          <option value="high">{t.finishHigh}</option>
                        </select>
                        {formData.finish_level && (
                          <p className="text-green-600 text-sm mt-1 animate-fade-in">
                            {formData.finish_level === 'high' ? 'Premium finishes for a luxury feel' : 
                               formData.finish_level === 'medium' ? 'Quality finishes with good value' : 
                               'Cost-effective basic finishes'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="furniture_standard" className="text-sm font-medium text-brand-black flex items-center space-x-2">
                          <span>{t.furnitureStandard}</span>
                          <span className="text-red-500">*</span>
                        </Label>
                        <select
                          id="furniture_standard"
                          value={formData.furniture_standard}
                          onChange={(e) => handleInputChange('furniture_standard', e.target.value)}
                          className={`mt-1 w-full p-3 border bg-white field-focus touch-optimization ${
                            formErrors.furniture_standard ? 'border-red-500' : 'border-gray-300'
                          }`}
                          data-testid="furniture-standard-select"
                        >
                          <option value="">{language === 'en' ? 'Select furniture standard' : language === 'nl' ? 'Selecteer meubel standaard' : 'Sélectionner standard mobilier'}</option>
                          <option value="low">{t.furnitureLow}</option>
                          <option value="medium">{t.furnitureMedium}</option>
                          <option value="high">{t.furnitureHigh}</option>
                        </select>
                        {formData.furniture_standard && (
                          <p className="text-green-600 text-sm mt-1 animate-fade-in">
                            {formData.furniture_standard === 'high' ? 'All new furniture for a fresh start' : 
                               formData.furniture_standard === 'medium' ? 'Mix of new and reused furniture' : 
                               'Cost-effective with maximum reuse'}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 3: AI Analysis & Floor Plan */}
                  {currentStep === 3 && (
                    <div className="space-y-6 animate-fade-in">
                      {/* Compelling message */}
                      <div className="bg-brand-light-grey border-l-4 border-brand-gold p-4 mb-4">
                        <p className="text-sm text-brand-black">
                          <strong>Get a personalized budget:</strong> Share your project details below for an AI-powered analysis tailored to your specific needs, this is what makes our calculator unique!
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="floor_plan_upload" className="text-sm font-medium text-brand-black flex items-center space-x-2">
                          <Upload className="w-4 h-4 text-brand-gold" />
                          <span>{t.floorPlanUpload}</span>
                        </Label>
                        <p className="text-xs text-brand-grey mt-1 mb-2">
                          Click to upload or drag and drop your floor plan
                        </p>
                        <div 
                          className="mt-1"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(true);
                          }}
                          onDragEnter={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(true);
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(false);
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(false);
                            
                            const file = e.dataTransfer.files?.[0] || null;
                            
                            if (file) {
                              // Validate file type
                              const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
                              if (!validTypes.includes(file.type)) {
                                addNotification('error', 'Invalid file type. Please upload an image (JPG, PNG, GIF, WEBP) or PDF.');
                                return;
                              }
                              
                              // Validate file size (max 10MB)
                              if (file.size > 10 * 1024 * 1024) {
                                addNotification('error', 'File is too large. Please upload a file smaller than 10MB.');
                                return;
                              }
                              
                              handleInputChange('floor_plan_file', file);
                              addNotification('success', 'Floor plan uploaded successfully!');
                            }
                          }}
                        >
                          <input
                            type="file"
                            id="floor_plan_upload"
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              
                              // Validate file size (max 10MB)
                              if (file && file.size > 10 * 1024 * 1024) {
                                addNotification('error', 'File is too large. Please upload a file smaller than 10MB.');
                                e.target.value = ''; // Clear the file input
                                return;
                              }
                              
                              handleInputChange('floor_plan_file', file);
                            }}
                            className="hidden"
                            data-testid="floor-plan-input"
                          />
                          <div
                            onClick={() => document.getElementById('floor_plan_upload')?.click()}
                            className={`w-full border-2 transition-all duration-200 p-8 text-center cursor-pointer touch-optimization ${
                              isDragging 
                                ? 'border-brand-gold bg-brand-gold/10 border-dashed' 
                                : formData.floor_plan_file
                                ? 'border-green-500 bg-green-50'
                                : 'border-brand-gold hover:bg-brand-gold/5'
                            }`}
                          >
                            <Upload className={`w-8 h-8 mx-auto mb-3 ${isDragging ? 'text-brand-gold animate-bounce' : 'text-brand-grey'}`} />
                            {isDragging ? (
                              <p className="text-brand-gold font-medium">Drop your file here</p>
                            ) : formData.floor_plan_file ? (
                              <div>
                                <p className="text-green-600 font-medium mb-1">✓ File uploaded</p>
                                <p className="text-xs text-brand-grey">{formData.floor_plan_file.name} ({Math.round(formData.floor_plan_file.size / 1024)} KB)</p>
                                <p className="text-xs text-brand-gold mt-2">Click to replace</p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-brand-black font-medium mb-1">Click to upload or drag & drop</p>
                                <p className="text-xs text-brand-grey">PDF, PNG, JPG up to 10MB</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="additional_info" className="text-sm font-medium text-brand-black">
                          {t.additionalInfo}
                        </Label>
                        <p className="text-xs text-brand-grey mt-1 mb-2">
                          Share details about your workspace needs for personalized AI recommendations
                        </p>
                        <Textarea
                          id="additional_info"
                          placeholder="e.g., Team size, collaboration needs, privacy requirements, sustainability goals..."
                          value={formData.additional_info}
                          onChange={(e) => handleInputChange('additional_info', e.target.value)}
                          className="mt-1 field-focus touch-optimization border-gray-300 min-h-[100px] resize-none text-sm md:text-base placeholder:text-xs md:placeholder:text-sm"
                          data-testid="additional-info-textarea"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 4: Contact Information */}
                  {currentStep === 4 && (
                    <div className="space-y-6 animate-slide-in-right">
                      {/* Company Name - Required with validation */}
                      <div>
                        <Label htmlFor="company" className="text-sm font-medium text-brand-black flex items-center space-x-2">
                          <span>Bedrijfsnaam / Company Name</span>
                          <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="company"
                            type="text"
                            placeholder="Uw bedrijfsnaam / Your Company Name"
                            value={contactData.company}
                            onChange={(e) => handleContactInputChange('company', e.target.value)}
                            onBlur={handleCompanyValidation}
                            className="mt-1 field-focus touch-optimization border-gray-300 pr-10"
                            data-testid="company-input"
                          />
                          {companyValidationStatus.loading && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <div className="spinner-small"></div>
                            </div>
                          )}
                          {companyValidationStatus.verified && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <span className="text-green-600">✓</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Validation Badge */}
                        {companyValidationStatus.message && (
                          <div className={`mt-2 text-xs p-2 rounded ${
                            companyValidationStatus.verified 
                              ? 'bg-green-50 text-green-700 border border-green-200' 
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}>
                            {companyValidationStatus.message}
                          </div>
                        )}
                      </div>

                      {/* Email - Optional */}
                      <div>
                        <Label htmlFor="email" className="text-sm font-medium text-brand-black flex items-center space-x-2">
                          <span>Email (Optioneel)</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john.doe@company.com"
                          value={contactData.email}
                          onChange={(e) => handleContactInputChange('email', e.target.value)}
                          className="mt-1 field-focus touch-optimization border-gray-300"
                          data-testid="email-input"
                        />
                        <p className="text-xs text-brand-grey mt-1">
                          Vul dit in als u de resultaten per email wilt ontvangen
                        </p>
                      </div>

                      {/* Privacy Notice - Only required if email is provided */}
                      {contactData.email && contactData.email.trim() !== '' && (
                        <div className="bg-brand-light-grey p-4 border">
                          <div className="flex items-start space-x-2">
                            <input
                              type="checkbox"
                              id="terms"
                              checked={agreedToTerms}
                              onChange={(e) => setAgreedToTerms(e.target.checked)}
                              className="mt-0.5"
                              data-testid="terms-checkbox"
                            />
                            <Label htmlFor="terms" className="text-sm text-brand-black cursor-pointer leading-relaxed">
                              <span className="font-medium">Ik ga akkoord dat Captif contact met mij mag opnemen over dit project.</span>
                              <span className="block text-xs text-brand-grey mt-1">Uw gegevens worden niet toegevoegd aan mailinglijsten.</span>
                            </Label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Error Display */}
                  {error && (
                    <ErrorState 
                      title="Validation Error"
                      message={error}
                      onRetry={() => setError('')}
                    />
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between pt-6 border-t border-gray-200 space-y-3 md:space-y-0">
                    <div>
                      {currentStep > 1 && (
                        <LoadingButton
                          onClick={goToPrevStep}
                          variant="ghost"
                          className="touch-optimization text-brand-grey hover:text-brand-black hover:bg-gray-50 text-sm"
                          disabled={loading}
                        >
                          ← Previous
                        </LoadingButton>
                      )}
                    </div>

                    <div className="flex space-x-2 md:space-x-3">
                      <LoadingButton 
                        onClick={resetCalculator} 
                        variant="ghost"
                        className="text-brand-grey hover:bg-gray-50 touch-optimization px-3 md:px-4 py-2 text-sm"
                        disabled={loading}
                      >
                        Reset
                      </LoadingButton>

                      {currentStep < totalSteps ? (
                        <LoadingButton 
                          onClick={goToNextStep} 
                          disabled={loading}
                          className="btn-gold-hover touch-optimization text-sm md:text-base"
                        >
                          Next →
                        </LoadingButton>
                      ) : (
                        <LoadingButton 
                          onClick={handleFinalCalculate} 
                          loading={loading}
                          disabled={!validateContactForm()}
                          className="w-full bg-gradient-to-r from-brand-black to-gray-800 text-brand-gold hover:from-brand-gold hover:to-yellow-500 hover:text-brand-black font-bold text-base md:text-lg px-4 md:px-8 py-3 md:py-4 shadow-xl border-2 border-brand-gold transition-all duration-300 transform hover:scale-[1.02] touch-optimization"
                          data-testid="calculate-button"
                        >
                          {loading ? (
                            <span className="flex items-center justify-center">
                              <div className="spinner mr-2 md:mr-3"></div>
                              <span className="text-sm md:text-base">Calculating Your Budget...</span>
                            </span>
                          ) : (
                            <span className="flex items-center justify-center">
                              <Calculator className="mr-2 md:mr-3 h-5 w-5 md:h-6 md:w-6" />
                              <span className="text-sm md:text-base">Calculate My Budget Estimate</span>
                            </span>
                          )}
                        </LoadingButton>
                      )}
                    </div>
                  </div>

                {/* Old contact form removed - now integrated in multi-step form */}
              </CardContent>
            </Card>

            )}
            
            {/* Results */}
            {result && (
              <div className="mt-8">
                <div className="bg-white border border-gray-200 shadow-2xl" data-testid="results-card">
                  {/* Header Section */}
                  <div className="bg-gradient-to-r from-brand-black to-gray-800 text-white p-8">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold mb-2">Your Budget Estimate</h2>
                      <p className="text-gray-400">
                        Based on {Math.round(result.surface)}m² {result.workplace_concept} workspace
                      </p>
                    </div>
                  </div>

                  {/* Main Budget Display */}
                  <div className="p-8 bg-gradient-to-br from-white via-white to-brand-gold/10">
                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                      {/* Price per m² */}
                      <div className="text-center">
                        <div className="text-sm text-brand-grey uppercase tracking-wider mb-2">Price per m²</div>
                        <div className="text-4xl font-bold text-brand-black mb-2" data-testid="price-per-m2">
                          €{formatEuropeanCurrency(result.price_per_m2)}
                        </div>
                        <div className="text-sm text-brand-grey">
                          Range: €{formatEuropeanCurrency(Math.round(result.price_per_m2 * 0.92))}-€{formatEuropeanCurrency(Math.round(result.price_per_m2 * 1.08))}
                        </div>
                      </div>
                      
                      {/* Total Budget */}
                      <div className="text-center">
                        <div className="text-sm text-brand-grey uppercase tracking-wider mb-2">Total Investment</div>
                        <div className="text-4xl font-bold text-brand-gold mb-2" data-testid="total-budget">
                          €{formatEuropeanCurrency(result.total_budget)}
                        </div>
                        <div className="text-sm text-brand-grey">
                          Range: €{formatEuropeanCurrency(Math.round(result.total_budget * 0.92))}-€{formatEuropeanCurrency(Math.round(result.total_budget * 1.08))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Budget Feedback Section - Option A */}
                  {!budgetFeedback && (
                    <div className="border-t border-gray-200 bg-white p-6">
                      <div className="max-w-2xl mx-auto text-center">
                        <h3 className="text-lg font-semibold text-brand-black mb-4">
                          Is this within your expected budget range?
                        </h3>
                        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                          <button
                            onClick={() => setBudgetFeedback('yes')}
                            className="w-full sm:w-auto px-6 py-3 bg-brand-gold text-brand-black hover:bg-brand-black hover:text-brand-gold font-semibold transition-all duration-200 text-sm sm:text-base"
                          >
                            ✓ Yes, looks good
                          </button>
                          <button
                            onClick={() => setBudgetFeedback('too-high')}
                            className="w-full sm:w-auto px-6 py-3 bg-white text-brand-black border-2 border-brand-gold hover:bg-brand-gold hover:text-brand-black font-semibold transition-all duration-200 text-sm sm:text-base"
                          >
                            It's higher than expected
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cost-Saving Tips (shown when budget is too high) */}
                  {budgetFeedback === 'too-high' && (
                    <div className="border-t border-gray-200 bg-gradient-to-br from-gray-50 to-white p-8">
                      <div className="max-w-3xl mx-auto">
                        <h3 className="text-xl font-bold text-brand-black mb-4">
                          Ways to Optimize Your Budget
                        </h3>
                        <p className="text-brand-grey mb-6">
                          Here are some proven strategies to reduce costs while maintaining quality:
                        </p>
                        
                        <div className="grid md:grid-cols-3 gap-4 mb-6">
                          {/* Tip 1 */}
                          <div className="bg-white p-4 border border-brand-gold/30 hover:border-brand-gold transition-all duration-200">
                            <div className="text-brand-gold font-bold mb-2">1. Adjust Finish Level</div>
                            <p className="text-sm text-brand-grey">
                              Standard finishes can reduce costs by 15-20% while still providing professional quality
                            </p>
                          </div>
                          
                          {/* Tip 2 */}
                          <div className="bg-white p-4 border border-brand-gold/30 hover:border-brand-gold transition-all duration-200">
                            <div className="text-brand-gold font-bold mb-2">2. Optimize Workspace Type</div>
                            <p className="text-sm text-brand-grey">
                              Open-plan concepts typically cost 10-15% less than cellular offices
                            </p>
                          </div>
                          
                          {/* Tip 3 */}
                          <div className="bg-white p-4 border border-brand-gold/30 hover:border-brand-gold transition-all duration-200">
                            <div className="text-brand-gold font-bold mb-2">3. Smart Furniture Choices</div>
                            <p className="text-sm text-brand-grey">
                              Standard furniture packages offer excellent value without compromising functionality
                            </p>
                          </div>
                        </div>

                        <div className="bg-brand-gold/10 border-l-4 border-brand-gold p-4">
                          <p className="text-brand-black font-semibold mb-2">
                            Let's discuss your options
                          </p>
                          <p className="text-sm text-brand-grey">
                            Our experts can help you find the perfect balance between budget and quality. We've successfully delivered projects across all budget ranges.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Thank you message (shown when budget is good) */}
                  {budgetFeedback === 'yes' && (
                    <div className="border-t border-gray-200 bg-white p-6">
                      <div className="max-w-2xl mx-auto text-center">
                        <h3 className="text-lg font-semibold text-brand-black mb-2">
                          Great! Let's make it happen
                        </h3>
                        <p className="text-brand-grey">
                          We're excited to help bring your office vision to life. Scroll down to explore our AI analysis and next steps.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* AI Insights & Actions Section - Redesigned for modern, clean look */}
                  <div className="border-t border-gray-200 bg-gradient-to-br from-gray-50 to-white p-8">
                    <div className="max-w-4xl mx-auto">
                      {result.ai_insights && (
                        <div className="mb-8">
                          {/* AI Analysis Card - Gold border, Captif logo */}
                          <div className="relative bg-white shadow-lg border-2 border-brand-gold overflow-hidden">
                            {/* Content */}
                            <div className="p-8">
                              {/* Header with Captif logo */}
                              <div className="flex items-center mb-6">
                                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white mr-4 shadow-md overflow-hidden flex-shrink-0">
                                  <img 
                                    src="https://customer-assets.emergentagent.com/job_budget-architect/artifacts/046b2tbv_CAPTIF_Circle%20Only_Black.png" 
                                    alt="Captif Logo" 
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                                <div>
                                  <h3 className="text-xl font-bold text-brand-black">AI Workplace Analysis</h3>
                                  <p className="text-sm text-brand-grey">Personalized insights for your project</p>
                                </div>
                              </div>
                              
                              {/* AI Insights Text - Clean typography, no left border */}
                              <div className="bg-gray-50 p-6 mb-6">
                                <div className="text-brand-black leading-relaxed text-base" dangerouslySetInnerHTML={{ __html: result.ai_insights }}></div>
                              </div>
                              
                              {/* CTA Button - Prominent */}
                              <Button
                                onClick={() => window.open('https://captif.be/en/our-work/', '_blank')}
                                className="bg-gradient-to-r from-brand-gold to-brand-gold/90 text-brand-black hover:from-brand-black hover:to-brand-black hover:text-brand-gold font-semibold px-8 py-3 shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                              >
                                View Our Work
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Next Steps Section - Cleaner grid */}
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Book Consultation & PDF Report */}
                        <div className="bg-white p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                          <h4 className="font-bold text-brand-black mb-2 flex items-center text-lg">
                            <Calendar className="h-6 w-6 mr-2 text-brand-gold" />
                            Free Consultation & Report
                          </h4>
                          <p className="text-sm text-brand-grey mb-5">
                            Book a 30-minute consultation or receive a detailed PDF report via email
                          </p>
                          <div className="space-y-3">
                            <Button 
                              className="w-full bg-white text-brand-gold border-2 border-brand-gold hover:bg-brand-gold hover:text-white font-semibold transition-all duration-200 py-3"
                              onClick={() => window.open('https://calendar.app.google/Tv9inBBtDRyR3byX9', '_blank')}
                              data-testid="book-consultation-button"
                            >
                              <Calendar className="mr-2 h-5 w-5" />
                              Book Free 30min Call
                            </Button>
                            <Button 
                              className="w-full bg-white text-brand-gold border-2 border-brand-gold hover:bg-brand-gold hover:text-white font-semibold transition-all duration-200 py-3"
                              onClick={handleGeneratePDF}
                              disabled={pdfLoading}
                              data-testid="pdf-report-button"
                            >
                              {pdfLoading ? (
                                <>
                                  <div className="spinner mr-2"></div>
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <FileDown className="mr-2 h-5 w-5" />
                                  {t.downloadPdf}
                                </>
                              )}
                            </Button>
                          </div>
                        </div>

                        {/* Contact */}
                        <div className="bg-white p-6 shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                          <h4 className="font-bold text-brand-black mb-2 flex items-center text-lg">
                            <Mail className="h-6 w-6 mr-2 text-brand-gold" />
                            Direct Contact
                          </h4>
                          <p className="text-sm text-brand-grey mb-5">
                            Ready to start? Contact us directly for detailed specifications and binding quotes
                          </p>
                          <div className="space-y-3">
                            <div className="flex items-center text-brand-grey hover:text-brand-gold transition-colors duration-200 cursor-pointer">
                              <Mail className="h-5 w-5 mr-3 text-brand-gold" />
                              <span className="font-medium">info@captif.be</span>
                            </div>
                            <div className="flex items-center text-brand-grey hover:text-brand-gold transition-colors duration-200 cursor-pointer">
                              <Phone className="h-5 w-5 mr-3 text-brand-gold" />
                              <span className="font-medium">+32 3 304 80 50</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer Disclaimer */}
                  <div className="bg-gray-50 border-t border-gray-200 px-8 py-5">
                    <div className="max-w-3xl mx-auto">
                      <p className="text-sm text-gray-700 text-center mb-3">
                        *Preliminary estimate based on market benchmarks and expertise.
                        {result.ai_insights && " AI analysis included for additional requirements."}
                      </p>
                      <p className="text-sm text-gray-800 text-center font-medium">
                        Excludes: <strong>HVAC systems, primary technical installations, structural work, and consultancy fees.</strong>
                      </p>
                      <p className="text-sm text-gray-600 text-center mt-3">
                        Contact us for accurate, binding quotes including all requirements.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Call to Action - Corrected Design */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-black">
        <div className="max-w-5xl mx-auto text-center">
          {/* Small "So you are" text above main heading */}
          <p className="text-gray-400 text-xs md:text-sm mb-2 uppercase tracking-wide">
            So you are
          </p>
          
          {/* Main Heading with Playfair Display "transform" - Corrected */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl text-white mb-8 leading-tight">
            <span className="font-normal lowercase">ready to </span>
            <span 
              className="text-brand-gold font-normal lowercase"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            >
              transform
            </span>
            <span className="font-normal lowercase"> your office?</span>
          </h2>
          
          <p className="text-gray-300 text-base mb-10 max-w-2xl mx-auto leading-relaxed">
            Get a detailed quote and personal consultation with our expert team, and we'll make your vision happen!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              onClick={() => {
                navigator.clipboard.writeText('info@captif.be').then(() => {
                  addNotification('success', 'Email address copied! Send us an email at info@captif.be');
                }).catch(() => {
                  addNotification('error', 'Could not copy. Email us at: info@captif.be');
                });
              }}
              className="inline-flex items-center justify-center px-6 py-2.5 text-sm border border-white text-white hover:bg-white hover:text-brand-black font-medium rounded-none transition-all duration-300 cursor-pointer uppercase tracking-wider"
              data-testid="contact-button"
            >
              INFO@CAPTIF.BE
            </button>
            <a 
              href="https://captif.be"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-2.5 text-sm border border-white text-white hover:bg-white hover:text-brand-black font-medium rounded-none transition-all duration-300 uppercase tracking-wider whitespace-nowrap"
              data-testid="website-button"
            >
              <Globe className="mr-2 h-4 w-4" />
              WEBSITE
            </a>
            <button
              onClick={resetCalculator}
              className="inline-flex items-center justify-center px-6 py-2.5 text-sm border border-white text-white hover:bg-white hover:text-brand-black font-medium rounded-none transition-all duration-300 uppercase tracking-wider"
              data-testid="start-again-button"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              START AGAIN
            </button>
          </div>
        </div>
      </section>

      {/* Footer - Corrected Design */}
      <footer className="bg-brand-black py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Image Carousel Section - Banner Style (taller) */}
          <div className="mb-12 h-56 md:h-72 overflow-hidden">
            <ImageCarousel images={ctaImages} interval={4000} className="h-full" />
          </div>
          
          {/* Main Footer Content */}
          <div className="text-center mb-6">
            {/* Logo - 1.5x bigger */}
            <div className="mb-5">
              <img 
                src="https://customer-assets.emergentagent.com/job_lets-continue-19/artifacts/k3h60ezg_CAPTIF_Logo_Black.png" 
                alt="Captif Office Architects" 
                className="h-24 w-auto mx-auto brightness-0 invert"
              />
            </div>
            
            {/* Tagline */}
            <p className="text-white text-sm leading-relaxed max-w-2xl mx-auto mb-5">
              Professional office interior design and build services.<br />
              Creating inspiring workspaces that enhance productivity and well-being.
            </p>
            
            {/* Offices Location */}
            <p className="text-brand-gold text-xs font-medium tracking-wider mb-6 uppercase">
              OFFICES IN ANTWERP & BRUSSELS
            </p>
            
            {/* Social Media Icons - Smaller, thinner */}
            <div className="flex justify-center items-center space-x-5 mb-6">
              <a 
                href="https://www.instagram.com/captif_office_architects?igsh=OWw3OHhhNmZ5dGtq"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-brand-gold transition-colors duration-200"
              >
                <Instagram className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <a 
                href="https://www.facebook.com/share/1CQFGQrbPi/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-brand-gold transition-colors duration-200"
              >
                <Facebook className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <a 
                href="https://www.linkedin.com/company/captif-office-architects/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-brand-gold transition-colors duration-200"
              >
                <Linkedin className="h-4 w-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="border-t border-gray-700 pt-6">
            <div className="text-center mb-4">
              <p className="text-white text-sm leading-relaxed max-w-4xl mx-auto mb-3">
                <strong>Important Disclaimer:</strong> Budget estimates provided by this calculator are preliminary estimations only and do not constitute binding offers. 
                Final project costs are subject to detailed design specifications, construction requirements, chosen techniques, and site conditions.
              </p>
              <p className="text-gray-300 text-sm max-w-4xl mx-auto">
                Estimates <strong>exclude HVAC systems, primary technical installations, structural work, and other building-related elements.</strong> Contact us for accurate, binding quotes.
              </p>
            </div>
            <div className="text-center mt-6">
              <p className="text-gray-400 text-xs opacity-60">
                © B12 Design all rights reserved
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Client-Only Tool (Internal Tool Components Removed)

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
